/**
 * Hive Keychain authentication service
 * Non-custodial authentication using Hive Keychain browser extension
 */
class KeychainAuth {
  /**
   * Check if Hive Keychain is installed
   * @returns {boolean}
   */
  isKeychainInstalled() {
    return typeof window !== 'undefined' && !!window.hive_keychain;
  }

  /**
   * Initialize Keychain connection
   */
  async initialize() {
    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        reject(new Error('Hive Keychain not installed'));
        return;
      }

      window.hive_keychain.requestHandshake(() => {
        resolve(true);
      });
    });
  }

  /**
   * Login with Keychain by verifying signature
   * @param {string} username - Hive username
   * @returns {Promise<Object>} User data
   */
  async login(username) {
    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        reject(new Error('Please install Hive Keychain extension'));
        return;
      }

      if (!username) {
        reject(new Error('Username is required'));
        return;
      }

      // Create a unique message to sign for verification
      const message = `Login to Flexus at ${Date.now()}`;
      
      window.hive_keychain.requestSignBuffer(
        username,
        message,
        'Posting',
        (response) => {
          if (response.success) {
            resolve({
              username,
              method: 'keychain',
              timestamp: Date.now()
            });
          } else {
            reject(new Error(response.message || 'Keychain authentication failed'));
          }
        }
      );
    });
  }

  /**
   * Login with Keychain account picker (no username required)
   * @returns {Promise<Object>} User data with selected account
   */
  async loginWithAccountPicker() {
    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        reject(new Error('Please install Hive Keychain extension'));
        return;
      }

      // Create a unique message to sign for verification
      const message = `Login to Flexus at ${Date.now()}`;
      
      // Use null for username to trigger Keychain's account picker
      window.hive_keychain.requestSignBuffer(
        null,
        message,
        'Posting',
        (response) => {
          if (response.success) {
            resolve({
              username: response.data.username,
              method: 'keychain',
              timestamp: Date.now()
            });
          } else {
            reject(new Error(response.message || 'Keychain authentication failed'));
          }
        }
      );
    });
  }

  /**
   * Post a comment using Keychain
   * @param {string} username - Hive username
   * @param {string} parentAuthor - Parent post/comment author
   * @param {string} parentPermlink - Parent post/comment permlink
   * @param {string} body - Comment body
   * @param {string} title - Post title (optional, for comments use empty string)
   * @returns {Promise<Object>} Transaction result
   */
  async comment(username, parentAuthor, parentPermlink, body, title = '') {
    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        reject(new Error('Hive Keychain not installed'));
        return;
      }

      window.hive_keychain.requestPost(
        username,
        title,
        body,
        parentAuthor,
        parentPermlink,
        '',
        '',
        '',
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message || 'Failed to post comment'));
          }
        }
      );
    });
  }

  /**
   * Vote using Keychain
   * @param {string} username - Hive username
   * @param {string} author - Post author
   * @param {string} permlink - Post permlink
   * @param {number} weight - Vote weight (0-10000, 10000 = 100%)
   * @returns {Promise<Object>} Transaction result
   */
  async vote(username, author, permlink, weight) {
    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        reject(new Error('Hive Keychain not installed'));
        return;
      }

      window.hive_keychain.requestVote(
        username,
        permlink,
        author,
        weight,
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message || 'Failed to vote'));
          }
        }
      );
    });
  }

  /**
   * Send custom JSON using Keychain
   * @param {string} username - Hive username
   * @param {string} id - Custom JSON ID
   * @param {string} keyType - Key type ('Posting' or 'Active')
   * @param {Object} json - JSON data
   * @returns {Promise<Object>} Transaction result
   */
  async customJson(username, id, keyType, json) {
    return new Promise((resolve, reject) => {
      if (!this.isKeychainInstalled()) {
        reject(new Error('Hive Keychain not installed'));
        return;
      }

      window.hive_keychain.requestCustomJson(
        username,
        id,
        keyType,
        JSON.stringify(json),
        'Custom JSON',
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message || 'Failed to send custom JSON'));
          }
        }
      );
    });
  }

  /**
   * Get Keychain download URL
   * @returns {string}
   */
  getDownloadUrl() {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    if (isFirefox) {
      return 'https://addons.mozilla.org/en-US/firefox/addon/hive-keychain/';
    }
    return 'https://chrome.google.com/webstore/detail/hive-keychain/jcacnejopjdphbnjgfaaobbfafkihpep';
  }
}

const keychainAuth = new KeychainAuth();
export default keychainAuth;
